

/**
 * Return the strongest charging level for a household phase/current selection.
 * Optional OBC rows remain excluded until the shopper explicitly enables them.
 */
const POWER_EPSILON = 0.01;

function numericValuesDiffer(left, right, epsilon = 0) {
  const leftNumber = Number(left);
  const rightNumber = Number(right);

  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return Math.abs(leftNumber - rightNumber) > epsilon;
  }

  return left !== right;
}

export function hasMeaningfulOptionalProfile(profile) {
  const standard = Array.isArray(profile?.standard) ? profile.standard : [];
  const optional = Array.isArray(profile?.optional) ? profile.optional : [];

  if (optional.length === 0) return false;

  return optional.some((optionalLevel) => {
    const standardLevel = standard.find(
      (level) =>
        Number(level.supply_phases) === Number(optionalLevel.supply_phases) &&
        Number(level.supply_current_a) === Number(optionalLevel.supply_current_a) &&
        Number(level.supply_voltage_v) === Number(optionalLevel.supply_voltage_v) &&
        !numericValuesDiffer(
          level.supply_power_kw,
          optionalLevel.supply_power_kw,
          POWER_EPSILON,
        ),
    );

    if (!standardLevel) return true;

    return (
      numericValuesDiffer(
        standardLevel.effective_power_kw,
        optionalLevel.effective_power_kw,
        POWER_EPSILON,
      ) ||
      numericValuesDiffer(
        standardLevel.charge_time_min,
        optionalLevel.charge_time_min,
      )
    );
  });
}

export function getActiveChargingLevels(profile, includeOptional) {
  if (!profile) return [];

  const standard = Array.isArray(profile.standard) ? profile.standard : [];
  const optional = Array.isArray(profile.optional) ? profile.optional : [];

  return includeOptional && hasMeaningfulOptionalProfile(profile)
    ? optional
    : standard;
}

export function getMaxChargingPower(levels) {
  const powers = (Array.isArray(levels) ? levels : [])
    .map((level) => Number(level.effective_power_kw))
    .filter((power) => Number.isFinite(power) && power > 0);

  return powers.length > 0 ? Math.max(...powers) : null;
}

export function getDisplayMaxAcPower(profile, vehicle, includeOptional) {
  // Preserve the existing vehicle-summary value for models without an
  // optional OBC profile. Only optional-profile vehicles switch dynamically.
  if (!hasMeaningfulOptionalProfile(profile)) {
    return vehicle?.charging?.max_ac_kw ?? null;
  }

  return getMaxChargingPower(getActiveChargingLevels(profile, includeOptional))
    ?? vehicle?.charging?.max_ac_kw
    ?? null;
}

export function findChargingLevel(profile, supply, includeOptional) {
  if (!profile || !supply) return null;
  const levels = getActiveChargingLevels(profile, includeOptional);
  const matches = levels.filter(
    (level) =>
      level.supply_phases === supply.phases &&
      (supply.currentA == null || level.supply_current_a === supply.currentA),
  );

  if (matches.length === 0) return null;
  return matches.reduce((best, level) => {
    const effectiveDifference = level.effective_power_kw - best.effective_power_kw;

    // Prefer the configuration that delivers the highest real charging power.
    if (Math.abs(effectiveDifference) > POWER_EPSILON) {
      return effectiveDifference > 0 ? level : best;
    }

    const ratedDifference = level.supply_power_kw - best.supply_power_kw;

    // If two configurations charge equally fast, avoid unnecessary EVSE capacity.
    if (Math.abs(ratedDifference) > POWER_EPSILON) {
      return ratedDifference < 0 ? level : best;
    }

    // If power is still tied, prefer the lower circuit-current requirement.
    return level.supply_current_a < best.supply_current_a ? level : best;
  });
}

/**
 * Apply connector, phase, availability, and scenario rules, then rank by the
 * portion of the selected vehicle capability each product can use.
 */
export function recommendProducts(products, profile, supply, scenario, includeOptional) {
  const level = findChargingLevel(profile, supply, includeOptional);
  if (!level) return [];

  return products
    .filter((product) => product.valid && product.available)
    .filter((product) => product.connector === profile.connector)
    .filter((product) => product.phases === supply.phases)
    .filter((product) => product.currentA === supply.currentA)
    .filter((product) => scenario == null || product.scenarios.includes(scenario))
    .map((product) => {
      const effectivePowerKw = Math.min(product.powerKw, level.effective_power_kw);
      return {
        product,
        effectivePowerKw,
        utilization: effectivePowerKw / level.effective_power_kw,
        limitedByVehicle: product.powerKw > level.effective_power_kw,
      };
    })
    .sort(
      (left, right) =>
        right.utilization - left.utilization ||
        right.effectivePowerKw - left.effectivePowerKw ||
        left.product.powerKw - right.product.powerKw,
    );
}

/**
 * Return compatible hardwired Wallboxes ranked by the charging power that the
 * selected vehicle can actually use. When two Wallboxes deliver the same
 * effective power, prefer the lower rated unit to avoid unnecessary oversizing.
 */
export function recommendWallboxes(products, profile, vehicleMaxKw) {
  const maxPowerKw = Number(vehicleMaxKw);
  if (!profile || !Number.isFinite(maxPowerKw) || maxPowerKw <= 0) return [];

  return products
    .filter((product) => product.valid && product.available)
    .filter((product) => product.installationType === 'hardwired')
    .filter((product) => product.connector === profile.connector)
    .map((product) => {
      const effectivePowerKw = Math.min(product.powerKw, maxPowerKw);
      return {
        product,
        effectivePowerKw,
        limitedByVehicle: product.powerKw > maxPowerKw + POWER_EPSILON,
        slowerThanVehicle: product.powerKw + POWER_EPSILON < maxPowerKw,
      };
    })
    .sort(
      (left, right) =>
        right.effectivePowerKw - left.effectivePowerKw ||
        left.product.powerKw - right.product.powerKw,
    );
}

export function replaceVehicleTokens(template, vehicle, activeMaxAcKw = null) {
  const year = vehicle.year.to == null
    ? `${vehicle.year.from}–Present`
    : vehicle.year.from === vehicle.year.to
      ? String(vehicle.year.from)
      : `${vehicle.year.from}–${vehicle.year.to}`;
  const name = [vehicle.brand, vehicle.model, vehicle.variant].filter(Boolean).join(' ');
  const values = {
    vehicle: name,
    brand: vehicle.brand,
    model: vehicle.model,
    variant: vehicle.variant || '',
    year,
    range: vehicle.range.real_km == null ? '—' : `${formatNumber(vehicle.range.real_km)} km`,
    battery: vehicle.battery.usable_kwh == null ? '—' : `${formatNumber(vehicle.battery.usable_kwh)} kWh`,
    max_ac: (activeMaxAcKw ?? vehicle.charging.max_ac_kw) == null
      ? '—'
      : `${formatNumber(activeMaxAcKw ?? vehicle.charging.max_ac_kw)} kW`,
    type: vehicle.type,
  };

  return Object.entries(values).reduce(
    (result, [token, value]) => result.replaceAll(`[${token}]`, value),
    template || '',
  );
}

function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const locale = typeof document !== 'undefined'
    ? (document.documentElement.lang || 'en')
    : 'en';

  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(Number(value));
}

export function replacePowerTokens(template, actualPowerKw, ratedPowerKw) {
  return (template || '')
    .replaceAll('[actual_power]', formatNumber(actualPowerKw))
    .replaceAll('[rated_power]', formatNumber(ratedPowerKw));
}

export function findDatabaseChargeTime(
  profile,
  effectivePowerKw,
  selectedLevel = null,
  includeOptional = false,
) {
  const targetPower = Number(effectivePowerKw);
  if (!profile || !Number.isFinite(targetPower) || targetPower <= 0) return null;

  const hasMatchingPower = (level) =>
    level &&
    Math.abs(Number(level.effective_power_kw) - targetPower) <= POWER_EPSILON;
  const hasValidTime = (level) =>
    Number.isFinite(Number(level?.charge_time_min)) &&
    Number(level.charge_time_min) > 0;

  // Use the selected database row first when its actual power matches.
  if (hasMatchingPower(selectedLevel) && hasValidTime(selectedLevel)) {
    return Number(selectedLevel.charge_time_min);
  }

  // A lower-output product can deliver less power than the selected connection.
  // In that case, reuse the database row for the same actual charging power.
  const levels = getActiveChargingLevels(profile, includeOptional);
  const match = levels.find((level) => hasMatchingPower(level) && hasValidTime(level));
  return match ? Number(match.charge_time_min) : null;
}

export function formatTime(minutes) {
  const totalMinutes = Math.round(Number(minutes));
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '—';
  if (totalMinutes < 60) return `${totalMinutes} min`;

  const hours = Math.floor(totalMinutes / 60);
  const remainder = totalMinutes % 60;
  return remainder ? `${hours}h ${String(remainder).padStart(2, '0')}m` : `${hours}h`;
}

function parseBoolean(value) {
  return value === 'true';
}

if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined') {
  class RheidonEvFit extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.state = {
        brands: [],
        modelsByBrand: new Map(),
        vehicles: [],
        vehiclesByBrandModel: new Map(),
        profiles: new Map(),
        brand: '',
        model: '',
        vehicle: null,
        profile: null,
        installationMode: null,
        supply: null,
        scenario: null,
        includeOptional: false,
      };

      // Invalidates in-flight brand/model/vehicle lookups when the shopper
      // switches to another selection before the slow data has arrived.
      this.selectionRequestId = 0;

      this.copy = {
        initial: this.querySelector('[data-status-message]')?.textContent.trim() || '',
        loading: this.dataset.loadingMessage,
        chooseVehicle: this.dataset.chooseVehicleMessage,
        error: this.dataset.errorMessage,
        noResults: this.dataset.noResultsMessage,
        selectSupply: this.dataset.selectSupplyMessage,
        noProducts: this.dataset.noProductsMessage,
        noThreePhase: this.dataset.noThreePhaseMessage,
        bestFit: this.dataset.bestFitLabel,
        compatible: this.dataset.compatibleLabel,
        circuit: this.dataset.circuitLabel,
        ratedPower: this.dataset.ratedPowerLabel,
        actualPower: this.dataset.actualPowerLabel,
        estimatedTime: this.dataset.estimatedTimeLabel,
        obcLimit: this.dataset.obcLimitMessage,
        capability: this.dataset.capabilityMessage,
        products: this.dataset.productsCountLabel,
        product: this.dataset.productCountLabel,
        hardwiredElectricalNote: this.dataset.hardwiredElectricalNote,
      };

      this.elements = {
        status: this.querySelector('[data-ev-status]'),
        statusMessage: this.querySelector('[data-status-message]'),
        results: this.querySelector('[data-ev-results]'),
        brand: this.querySelector('[data-ev-brand]'),
        model: this.querySelector('[data-ev-model]'),
        vehicle: this.querySelector('[data-ev-vehicle]'),
        vehicleLabel: this.querySelector('[data-ev-vehicle-label]'),
        vehicleName: this.querySelector('[data-vehicle-name]'),
        profileRows: this.querySelector('[data-profile-rows]'),
        optionalWrap: this.querySelector('[data-optional-obc-wrap]'),
        standardObcButton: this.querySelector('[data-obc-profile="standard"]'),
        optionalObcButton: this.querySelector('[data-obc-profile="optional"]'),
        standardObcPower: this.querySelector('[data-standard-obc-power]'),
        optionalObcPower: this.querySelector('[data-optional-obc-power]'),
        productCount: this.querySelector('[data-product-count]'),
        productNotice: this.querySelector('[data-product-notice]'),
        scenarioGroup: this.querySelector('[data-scenario-group]'),
        sheetLayer: this.querySelector('[data-sheet-layer]'),
        sheet: this.querySelector('[data-sheet]'),
        sheetTitle: this.querySelector('[data-sheet-title]'),
        sheetOptions: this.querySelector('[data-sheet-options]'),
        supplyImageDialog: this.querySelector('[data-supply-image-dialog]'),
        supplyImageDialogImage: this.querySelector('[data-supply-image-dialog-image]'),
        supplyImageDialogCaption: this.querySelector('[data-supply-image-dialog-caption]'),
      };

      this.placeholders = {
        brand: this.elements.brand.options[0]?.textContent || '',
        model: this.elements.model.options[0]?.textContent || '',
        vehicle: this.elements.vehicle.options[0]?.textContent || '',
      };

      this.productCards = [...this.querySelectorAll('[data-product-card]')];
      this.products = this.productCards.map((card) => this.parseProduct(card));
      this.bindEvents();
      this.initializeData().catch((error) => this.handleError(error));
    }

    disconnectedCallback() {
      if (!this.elements?.sheetLayer?.hidden) {
        document.body.classList.remove('rheidon-ev-fit-sheet-open');
      }
      this.closeSupplyImageDialog();
    }

    bindEvents() {
      this.elements.brand.addEventListener('change', () => this.selectBrand(this.elements.brand.value));
      this.elements.model.addEventListener('change', () => {
        this.selectModel(this.elements.model.value).catch((error) => this.handleError(error));
      });
      this.elements.vehicle.addEventListener('change', () => {
        this.selectVehicle(this.elements.vehicle.value).catch((error) => this.handleError(error));
      });

      this.addEventListener('click', (event) => {
        const imageZoomButton = event.target.closest('[data-supply-image-zoom]');
        if (imageZoomButton) {
          this.openSupplyImageDialog(imageZoomButton);
          return;
        }

        if (event.target.closest('[data-supply-image-close]')) {
          this.closeSupplyImageDialog();
          return;
        }

        if (event.target === this.elements.supplyImageDialog) {
          this.closeSupplyImageDialog();
          return;
        }

        const trigger = event.target.closest('[data-sheet-trigger]');
        if (trigger && !trigger.disabled) {
          this.openSheet(trigger.dataset.sheetTrigger, trigger);
          return;
        }

        const obcButton = event.target.closest('[data-obc-profile]');
        if (obcButton) {
          const useOptional = obcButton.dataset.obcProfile === 'optional';
          const hasOptional = hasMeaningfulOptionalProfile(this.state.profile);

          if (useOptional && !hasOptional) return;

          this.state.includeOptional = useOptional;
          this.renderFilterState();
          this.renderVehicleMetrics();
          this.renderTable();
          this.renderProducts();
          this.renderInformation();
          return;
        }

        const hardwiredButton = event.target.closest('[data-hardwired-choice]');
        if (hardwiredButton) {
          const isSelected = this.state.installationMode === 'hardwired';
          this.state.installationMode = isSelected ? null : 'hardwired';
          this.state.supply = null;
          this.state.scenario = null;
          this.renderFilterState();
          this.renderTable();
          this.renderProducts();
          return;
        }

        const supplyButton = event.target.closest('[data-supply]');
        if (supplyButton) {
          const phases = Number(supplyButton.dataset.supply);
          const currentA = Number(supplyButton.dataset.current);
          const isSelected =
            this.state.supply?.phases === phases &&
            this.state.supply?.currentA === currentA;

          if (![1, 3].includes(phases) || ![16, 32].includes(currentA)) return;

          this.state.installationMode = isSelected ? null : 'portable';
          this.state.supply = isSelected ? null : { phases, currentA };
          this.renderFilterState();
          this.renderTable();
          this.renderProducts();
          return;
        }

        const scenarioButton = event.target.closest('[data-scenario]');
        if (scenarioButton) {
          const scenario = scenarioButton.dataset.scenario;
          this.state.scenario = this.state.scenario === scenario ? null : scenario;
          this.renderFilterState();
          this.renderProducts();
          return;
        }

        if (event.target.closest('[data-clear-filters]')) {
          this.resetFilters();
        }
      });

      this.elements.sheetLayer.addEventListener('click', (event) => {
        if (event.target.closest('[data-sheet-close]')) this.closeSheet();
      });
      this.elements.sheetLayer.addEventListener('keydown', (event) => this.handleSheetKeydown(event));
    }

    openSupplyImageDialog(trigger) {
      const { supplyImageDialog, supplyImageDialogImage, supplyImageDialogCaption } = this.elements;
      if (!supplyImageDialog || !supplyImageDialogImage || !trigger?.dataset.imageSrc) return;

      const imageAlt = trigger.dataset.imageAlt || '';
      const previewImage = trigger.closest('.rheidon-ev-fit__supply-card')?.querySelector('.rheidon-ev-fit__choice-image');
      const previewSrc = previewImage ? previewImage.currentSrc || previewImage.src : '';
      const fullSrc = new URL(trigger.dataset.imageSrc, document.baseURI).href;
      const requestId = (this.supplyImageRequestId || 0) + 1;
      this.supplyImageRequestId = requestId;

      supplyImageDialogImage.src = previewSrc || fullSrc;
      supplyImageDialogImage.alt = imageAlt;
      if (supplyImageDialogCaption) supplyImageDialogCaption.textContent = imageAlt;

      if (typeof supplyImageDialog.showModal === 'function') {
        if (!supplyImageDialog.open) supplyImageDialog.showModal();
      } else {
        supplyImageDialog.setAttribute('open', '');
      }

      if (supplyImageDialogImage.src === fullSrc) return;

      const fullImage = new Image();
      fullImage.decoding = 'async';
      fullImage.onload = () => {
        if (this.supplyImageRequestId !== requestId || !supplyImageDialog.open) return;
        supplyImageDialogImage.src = fullImage.src;
      };
      fullImage.src = fullSrc;
    }

    closeSupplyImageDialog() {
      const dialog = this.elements?.supplyImageDialog;
      if (!dialog?.open) return;

      this.supplyImageRequestId = (this.supplyImageRequestId || 0) + 1;

      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    async initializeData() {
      const [brands, models] = await Promise.all([
        this.fetchJson(this.dataset.brandsUrl),
        this.fetchJson(this.dataset.modelsUrl),
      ]);

      this.state.brands = brands;
      this.state.modelsByBrand = new Map(models.map((entry) => [entry.brand, entry.models]));
      this.fillSelect(this.elements.brand, brands, this.placeholders.brand, (brand) => brand, (brand) => brand);
      this.syncMobileTriggers();

      const deepLinkedId = new URL(window.location.href).searchParams.get('vehicle');
      if (deepLinkedId) await this.applyVehicleDeepLink(deepLinkedId);
    }

    async fetchJson(url) {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Vehicle data request failed with ${response.status}`);
      return response.json();
    }

    async loadVehicles() {
      if (!this.vehiclesPromise) {
        this.vehiclesPromise = this.fetchJson(this.dataset.vehiclesUrl).then((vehicles) => {
          this.state.vehicles = vehicles;
          this.state.vehiclesByBrandModel = new Map();
          vehicles.forEach((vehicle) => {
            const key = `${vehicle.brand}\u0000${vehicle.model}`;
            const list = this.state.vehiclesByBrandModel.get(key) || [];
            list.push(vehicle);
            this.state.vehiclesByBrandModel.set(key, list);
          });
          return vehicles;
        }).catch((error) => {
          this.vehiclesPromise = null;
          throw error;
        });
      }
      return this.vehiclesPromise;
    }

    async loadProfiles() {
      if (!this.profilesPromise) {
        this.profilesPromise = this.fetchJson(this.dataset.profilesUrl).then((profiles) => {
          this.state.profiles = new Map(profiles.map((profile) => [String(profile.vehicle_id), profile]));
          return this.state.profiles;
        }).catch((error) => {
          this.profilesPromise = null;
          throw error;
        });
      }
      return this.profilesPromise;
    }

    fillSelect(select, values, placeholder, valueFor, labelFor) {
      const fragment = document.createDocumentFragment();
      const initial = document.createElement('option');
      initial.value = '';
      initial.textContent = placeholder;
      fragment.append(initial);

      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = valueFor(value);
        option.textContent = labelFor(value);
        fragment.append(option);
      });

      select.replaceChildren(fragment);
      select.disabled = values.length === 0;
    }

    selectBrand(brand) {
      this.selectionRequestId += 1;
      this.state.brand = brand;
      this.state.model = '';
      this.state.vehicle = null;
      this.state.profile = null;
      this.updateVehicleQuery(null);

      const models = brand ? this.state.modelsByBrand.get(brand) || [] : [];
      this.fillSelect(this.elements.model, models, this.placeholders.model, (model) => model, (model) => model);
      this.fillSelect(this.elements.vehicle, [], this.placeholders.vehicle, String, String);
      this.elements.brand.value = brand;
      this.resetFilters(false);
      this.showStatus(this.copy.initial, false, 'empty');
      this.syncMobileTriggers();
    }

    async selectModel(model) {
      const requestId = ++this.selectionRequestId;
      this.state.model = model;
      this.state.vehicle = null;
      this.state.profile = null;
      this.updateVehicleQuery(null);
      this.elements.model.value = model;
      this.fillSelect(this.elements.vehicle, [], this.placeholders.vehicle, String, String);
      this.resetFilters(false);
      this.showStatus(this.copy.initial, false, 'empty');
      this.syncMobileTriggers();

      if (!model || !this.state.brand) return;
      this.showStatus(this.copy.loading, false, 'loading');
      await this.loadVehicles();
      if (requestId !== this.selectionRequestId || this.state.model !== model) return;

      const matches = this.getVehicleMatches();
      this.fillSelect(
        this.elements.vehicle,
        matches,
        this.placeholders.vehicle,
        (vehicle) => String(vehicle.id),
        (vehicle) => this.vehicleOptionLabel(vehicle),
      );
      this.showStatus(this.copy.chooseVehicle, false, 'message');
      this.syncMobileTriggers();
    }

    async selectVehicle(vehicleId) {
      this.elements.vehicle.value = vehicleId;
      const requestId = ++this.selectionRequestId;
      if (!vehicleId) {
        this.state.vehicle = null;
        this.state.profile = null;
        this.updateVehicleQuery(null);
        this.resetFilters(false);
        this.showStatus(this.state.model ? this.copy.chooseVehicle : this.copy.initial, false, this.state.model ? 'message' : 'empty');
        this.syncMobileTriggers();
        return;
      }

      this.showStatus(this.copy.loading, false, 'loading');
      await Promise.all([this.loadVehicles(), this.loadProfiles()]);
      if (requestId !== this.selectionRequestId) return;
      const vehicle = this.state.vehicles.find((candidate) => String(candidate.id) === String(vehicleId));
      const profile = this.state.profiles.get(String(vehicleId));

      if (!vehicle || !profile) {
        this.state.vehicle = vehicle || null;
        this.state.profile = null;
        this.showStatus(this.copy.noResults, true, 'message');
        return;
      }

      this.state.vehicle = vehicle;
      this.state.profile = profile;
      this.resetFilters(false);
      this.renderVehicle();
      this.updateVehicleQuery(vehicle.id);
      this.syncMobileTriggers();
    }

    async applyVehicleDeepLink(vehicleId) {
      this.showStatus(this.copy.loading, false, 'loading');
      await this.loadVehicles();
      const vehicle = this.state.vehicles.find((candidate) => String(candidate.id) === String(vehicleId));
      if (!vehicle) {
        this.showStatus(this.copy.initial, false, 'empty');
        return;
      }

      this.selectBrand(vehicle.brand);
      this.elements.model.value = vehicle.model;
      await this.selectModel(vehicle.model);
      this.elements.vehicle.value = String(vehicle.id);
      await this.selectVehicle(String(vehicle.id));
    }

    getVehicleMatches() {
      return this.state.vehiclesByBrandModel.get(`${this.state.brand}\u0000${this.state.model}`) || [];
    }

    vehicleOptionLabel(vehicle) {
      const year = vehicle.year.to == null
        ? `${vehicle.year.from}–Present`
        : vehicle.year.from === vehicle.year.to
          ? String(vehicle.year.from)
          : `${vehicle.year.from}–${vehicle.year.to}`;
      return `${vehicle.variant || 'Standard version'} · ${year}`;
    }

    renderVehicle() {
      const { vehicle, profile } = this.state;
      if (!vehicle || !profile) return;

      this.elements.status.hidden = true;
      this.classList.remove('is-empty');
      this.elements.results.hidden = false;
      this.elements.vehicleName.textContent = [vehicle.brand, vehicle.model, vehicle.variant].filter(Boolean).join(' ');

      this.renderObcOptions();
      this.renderVehicleMetrics();
      this.renderFilterState();
      this.renderTable();
      this.renderProducts();
      this.renderInformation();
    }

    renderObcOptions() {
      const profile = this.state.profile;
      if (!profile) return;

      const standardLevels = Array.isArray(profile.standard) ? profile.standard : [];
      const optionalLevels = Array.isArray(profile.optional) ? profile.optional : [];
      const hasOptional = hasMeaningfulOptionalProfile(profile);

      this.elements.optionalWrap.hidden = !hasOptional;
      this.elements.optionalObcButton.disabled = !hasOptional;

      if (!hasOptional) {
        this.state.includeOptional = false;
        return;
      }

      const standardPower = getMaxChargingPower(standardLevels);
      const optionalPower = getMaxChargingPower(optionalLevels);

      this.elements.standardObcPower.textContent =
        standardPower == null ? '—' : `${formatNumber(standardPower)} kW`;
      this.elements.optionalObcPower.textContent =
        optionalPower == null ? '—' : `${formatNumber(optionalPower)} kW`;
    }

    renderVehicleMetrics() {
      const { vehicle, profile, includeOptional } = this.state;
      if (!vehicle || !profile) return;

      const activeMaxAcKw = getDisplayMaxAcPower(
        profile,
        vehicle,
        includeOptional,
      );
      const metricValues = [
        vehicle.range.real_km == null ? '—' : `${formatNumber(vehicle.range.real_km)} km`,
        vehicle.battery.usable_kwh == null ? '—' : `${formatNumber(vehicle.battery.usable_kwh)} kWh`,
        activeMaxAcKw == null ? '—' : `${formatNumber(activeMaxAcKw)} kW`,
        vehicle.type,
      ];

      this.querySelectorAll('[data-metric-value]').forEach((element, index) => {
        element.textContent = metricValues[index] || '—';
      });
    }

    renderTable() {
      const profile = this.state.profile;
      if (!profile) return;

      const hasOptional = hasMeaningfulOptionalProfile(profile);
      const isOptionalProfile = this.state.includeOptional && hasOptional;
      const rows = getActiveChargingLevels(profile, isOptionalProfile).map((level) => ({
        level,
        optional: isOptionalProfile,
      }));
      // Do not imply a personalized recommendation until the shopper selects
      // the available household power connection.
      const best = this.state.supply
        ? findChargingLevel(profile, this.state.supply, this.state.includeOptional)
        : null;
      const fragment = document.createDocumentFragment();

      rows.forEach(({ level, optional }) => {
        const row = document.createElement('tr');
        const isBest = best === level;
        if (isBest) row.classList.add('is-best');

        const phaseLabel = level.supply_phases === 3 ? 'Three-phase' : 'Single-phase';
        const configuration = `${optional ? 'Optional*' : 'Standard*'} · ${phaseLabel} ${level.supply_current_a}A`;
        const limitedByVehicle =
          level.supply_power_kw > level.effective_power_kw + POWER_EPSILON;
        const suggestion = limitedByVehicle
          ? replacePowerTokens(
              this.copy.obcLimit,
              level.effective_power_kw,
              level.supply_power_kw,
            ).replace('. ', '.\n')
          : isBest
            ? this.copy.capability
            : this.copy.compatible;

        row.append(
          this.createTableCell(configuration, `${level.supply_voltage_v}V`),
          this.createTableCell(`${this.copy.actualPower}: ${formatNumber(level.effective_power_kw)} kW`, `${this.copy.ratedPower}: ${formatNumber(level.supply_power_kw)} kW`),
          this.createTableCell(`${this.copy.estimatedTime}: ${formatTime(level.charge_time_min)}`),
          this.createTableCell(suggestion),
        );
        fragment.append(row);
      });

      this.elements.profileRows.replaceChildren(fragment);
    }

    createTableCell(mainText, subText = '') {
      const cell = document.createElement('td');
      const main = document.createElement('span');
      main.className = 'rheidon-ev-fit__table-cell-main';
      main.textContent = mainText;
      cell.append(main);
      if (subText) {
        const sub = document.createElement('span');
        sub.className = 'rheidon-ev-fit__table-cell-sub';
        sub.textContent = subText;
        cell.append(sub);
      }
      return cell;
    }

    renderFilterState() {
      this.querySelectorAll('[data-hardwired-choice]').forEach((button) => {
        button.setAttribute('aria-pressed', String(this.state.installationMode === 'hardwired'));
      });
      this.querySelectorAll('[data-supply]').forEach((button) => {
        const phases = Number(button.dataset.supply);
        const currentA = Number(button.dataset.current);
        const isSelected =
          this.state.supply?.phases === phases &&
          this.state.supply?.currentA === currentA;
        button.setAttribute('aria-pressed', String(isSelected));
      });
      this.querySelectorAll('[data-scenario]').forEach((button) => {
        button.setAttribute('aria-pressed', String(this.state.scenario === button.dataset.scenario));
      });
      this.querySelectorAll('[data-obc-profile]').forEach((button) => {
        const isOptionalButton = button.dataset.obcProfile === 'optional';
        const isSelected = isOptionalButton
          ? this.state.includeOptional
          : !this.state.includeOptional;
        button.setAttribute('aria-pressed', String(isSelected));
      });
      if (this.elements.scenarioGroup) {
        this.elements.scenarioGroup.hidden = this.state.installationMode === 'hardwired';
      }
    }

    renderProducts() {
      const { vehicle, profile, installationMode, supply, scenario, includeOptional } = this.state;
      this.productCards.forEach((card) => {
        card.hidden = true;
        card.classList.remove('is-best');
        card.style.order = '';
        const badge = card.querySelector('[data-best-fit]');
        if (badge) badge.hidden = true;
        const productNote = card.querySelector('[data-product-note]');
        if (productNote) {
          productNote.hidden = true;
          productNote.textContent = '';
          delete productNote.dataset.state;
        }
      });

      if (!vehicle || !profile) {
        this.setProductResult([], '');
        return;
      }

      if (installationMode === 'hardwired') {
        const vehicleMaxKw = getDisplayMaxAcPower(profile, vehicle, includeOptional);
        const results = recommendWallboxes(this.products, profile, vehicleMaxKw);
        this.renderWallboxResults(results, profile, includeOptional);
        return;
      }

      if (!supply) {
        this.setProductResult([], this.copy.selectSupply);
        return;
      }

      const level = findChargingLevel(profile, supply, includeOptional);
      if (!level) {
        this.setProductResult([], supply.phases === 3 ? this.copy.noThreePhase : this.copy.noProducts);
        return;
      }

      const results = recommendProducts(this.products, profile, supply, scenario, includeOptional);
      results.forEach((result, index) => {
        const card = result.product.card;
        card.hidden = false;
        card.style.order = String(index);
        card.classList.toggle('is-best', index === 0);
        const badge = card.querySelector('[data-best-fit]');
        if (badge) badge.hidden = index !== 0;

        const facts = [
          `${this.copy.ratedPower}: ${formatNumber(result.product.powerKw)} kW`,
          `${this.copy.actualPower}: ${formatNumber(result.effectivePowerKw)} kW`,
          `${this.copy.circuit}: ${result.product.currentA}A`,
        ];
        const databaseChargeTime = findDatabaseChargeTime(
          profile,
          result.effectivePowerKw,
          level,
          includeOptional,
        );
        facts.push(`${this.copy.estimatedTime}: ${formatTime(databaseChargeTime)}`);
        card.querySelector('[data-product-facts]').textContent = facts.join('\n');

        const productNote = card.querySelector(
          '[data-product-note]',
        );

        if (productNote) {
          if (result.limitedByVehicle) {
            productNote.textContent = replacePowerTokens(
              this.copy.obcLimit,
              result.effectivePowerKw,
              result.product.powerKw,
            );

            productNote.hidden = false;
            productNote.dataset.state = 'limited';
          } else {
            productNote.textContent = '';
            productNote.hidden = true;
            delete productNote.dataset.state;
          }
        }
      });

      this.setProductResult(results, results.length ? '' : this.copy.noProducts);
    }

    renderWallboxResults(results, profile, includeOptional) {
      results.forEach((result, index) => {
        const card = result.product.card;
        card.hidden = false;
        card.style.order = String(index);
        card.classList.toggle('is-best', index === 0);
        const badge = card.querySelector('[data-best-fit]');
        if (badge) badge.hidden = index !== 0;

        const facts = [
          `${this.copy.ratedPower}: ${formatNumber(result.product.powerKw)} kW`,
          `${this.copy.actualPower}: ${formatNumber(result.effectivePowerKw)} kW`,
        ];
        const databaseChargeTime = findDatabaseChargeTime(
          profile,
          result.effectivePowerKw,
          null,
          includeOptional,
        );
        facts.push(`${this.copy.estimatedTime}: ${formatTime(databaseChargeTime)}`);
        card.querySelector('[data-product-facts]').textContent = facts.join('\n');

        const productNote = card.querySelector('[data-product-note]');
        if (productNote) {
          if (result.limitedByVehicle) {
            productNote.textContent = replacePowerTokens(
              this.copy.obcLimit,
              result.effectivePowerKw,
              result.product.powerKw,
            );
            productNote.hidden = false;
            productNote.dataset.state = 'limited';
          } else {
            productNote.textContent = '';
            productNote.hidden = true;
            delete productNote.dataset.state;
          }
        }
      });

      this.setProductResult(
        results,
        results.length ? this.copy.hardwiredElectricalNote : this.copy.noProducts,
      );
    }

    setProductResult(results, notice) {
      const count = results.length;
      this.elements.productCount.textContent = `${count} ${count === 1 ? this.copy.product : this.copy.products}`;
      this.elements.productNotice.textContent = notice || '';
    }

    renderInformation() {
      const { vehicle, profile, includeOptional } = this.state;
      if (!vehicle || !profile) return;
      const activeMaxAcKw = getDisplayMaxAcPower(
        profile,
        vehicle,
        includeOptional,
      );
      this.querySelectorAll('[data-information-item]').forEach((item) => {
        item.querySelector('[data-information-heading]').textContent = replaceVehicleTokens(
          item.dataset.headingTemplate,
          vehicle,
          activeMaxAcKw,
        );
        item.querySelector('[data-information-body]').textContent = replaceVehicleTokens(
          item.dataset.bodyTemplate,
          vehicle,
          activeMaxAcKw,
        );
      });
    }

    parseProduct(card) {
      const powerKw = Number(card.dataset.powerKw);
      const phases = Number(card.dataset.phases);
      const currentA = Number(card.dataset.currentA);
      const connector = card.dataset.connector;
      const installationType = card.dataset.installationType || 'portable';
      const isHardwired = installationType === 'hardwired';
      const scenarios = [
        ...(parseBoolean(card.dataset.scenarioHome) ? ['home'] : []),
        ...(parseBoolean(card.dataset.scenarioOutdoor) ? ['outdoor'] : []),
      ];
      const valid =
        Number.isFinite(powerKw) &&
        powerKw > 0 &&
        ['portable', 'hardwired'].includes(installationType) &&
        ['TYPE1', 'TYPE2'].includes(connector) &&
        (isHardwired || (
          [1, 3].includes(phases) &&
          Number.isFinite(currentA) &&
          scenarios.length > 0
        ));
      if (!valid) console.warn('Rheidon EV fit finder excluded a product with incomplete compatibility settings.', card.dataset.productId);

      return {
        card,
        valid,
        available: parseBoolean(card.dataset.available),
        powerKw,
        phases,
        currentA,
        connector,
        installationType,
        scenarios,
      };
    }

    resetFilters(render = true) {
      this.state.installationMode = null;
      this.state.supply = null;
      this.state.scenario = null;
      this.state.includeOptional = false;
      if (render && this.state.vehicle) {
        this.renderFilterState();
        this.renderVehicleMetrics();
        this.renderTable();
        this.renderProducts();
        this.renderInformation();
      }
    }

    showStatus(message, isError = false, state = 'message') {
      this.elements.results.hidden = true;
      this.elements.status.hidden = false;
      this.elements.status.dataset.state = state;
      this.elements.status.classList.toggle('is-error', isError);
      this.elements.statusMessage.textContent = message;
      // Keep the selector layout stable while the user is choosing a brand,
      // model, or version. The result view removes this state in renderVehicle().
      this.classList.toggle('is-empty', !this.state.vehicle);
    }

    handleError(error) {
      console.error('Rheidon EV fit finder:', error);
      this.showStatus(this.copy.error, true, 'error');
    }

    syncMobileTriggers() {
      const labels = {
        brand: this.state.brand || this.placeholders.brand,
        model: this.state.model || this.placeholders.model,
        vehicle: this.state.vehicle ? this.vehicleOptionLabel(this.state.vehicle) : this.placeholders.vehicle,
      };
      Object.entries(labels).forEach(([kind, label]) => {
        const element = this.querySelector(`[data-trigger-label="${kind}"]`);
        if (element) element.textContent = label;
      });
      const modelTrigger = this.querySelector('[data-sheet-trigger="model"]');
      const vehicleTrigger = this.querySelector('[data-sheet-trigger="vehicle"]');
      if (modelTrigger) modelTrigger.disabled = !this.state.brand;
      if (vehicleTrigger) {
        vehicleTrigger.disabled = !this.state.brand || !this.state.model;
      }
    }

    async openSheet(kind, trigger) {
      let values = [];
      let title = '';
      if (kind === 'brand') {
        values = this.state.brands.map((brand) => ({ value: brand, label: brand }));
        title = this.dataset.chooseBrandTitle;
      } else if (kind === 'model') {
        values = (this.state.modelsByBrand.get(this.state.brand) || []).map((model) => ({ value: model, label: model }));
        title = this.dataset.chooseModelTitle;
      } else if (kind === 'vehicle') {
        await this.loadVehicles();
        values = this.getVehicleMatches().map((vehicle) => ({ value: String(vehicle.id), label: this.vehicleOptionLabel(vehicle) }));
        title = this.dataset.chooseVehicleTitle;
      }

      if (!values.length) return;
      this.activeSheetTrigger = trigger;
      this.activeSheetKind = kind;
      this.elements.sheetTitle.textContent = title;
      const fragment = document.createDocumentFragment();
      values.forEach((item) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rheidon-ev-fit__sheet-option';
        button.dataset.sheetValue = item.value;
        const label = document.createElement('span');
        label.textContent = item.label;
        const icon = document.createElement('img');
        icon.src = this.dataset.chevronUrl;
        icon.width = 20;
        icon.height = 20;
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        button.append(label, icon);
        button.addEventListener('click', () => this.chooseSheetValue(item.value));
        fragment.append(button);
      });
      this.elements.sheetOptions.replaceChildren(fragment);
      this.elements.sheetLayer.hidden = false;
      document.body.classList.add('rheidon-ev-fit-sheet-open');
      requestAnimationFrame(() => {
        const selected = this.elements.sheetOptions.querySelector(`[data-sheet-value="${CSS.escape(this.currentValueForKind(kind))}"]`);
        (selected || this.elements.sheetOptions.querySelector('button') || this.elements.sheet).focus();
      });
    }

    currentValueForKind(kind) {
      if (kind === 'brand') return this.state.brand;
      if (kind === 'model') return this.state.model;
      return this.state.vehicle ? String(this.state.vehicle.id) : '';
    }

    async chooseSheetValue(value) {
      const kind = this.activeSheetKind;
      this.closeSheet(false);
      try {
        if (kind === 'brand') {
          this.selectBrand(value);
        } else if (kind === 'model') {
          await this.selectModel(value);
        } else if (kind === 'vehicle') {
          await this.selectVehicle(value);
        }
      } catch (error) {
        this.handleError(error);
      }
      this.activeSheetTrigger?.focus();
    }

    closeSheet(restoreFocus = true) {
      if (this.elements.sheetLayer.hidden) return;
      this.elements.sheetLayer.hidden = true;
      document.body.classList.remove('rheidon-ev-fit-sheet-open');
      if (restoreFocus) this.activeSheetTrigger?.focus();
    }

    handleSheetKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSheet();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.elements.sheet.querySelectorAll('button:not([disabled]), [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) {
        event.preventDefault();
        this.elements.sheet.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    updateVehicleQuery(vehicleId) {
      const url = new URL(window.location.href);
      if (vehicleId == null || vehicleId === '') {
        url.searchParams.delete('vehicle');
      } else {
        url.searchParams.set('vehicle', String(vehicleId));
      }
      window.history.replaceState({}, '', url);
    }
  }

  if (!customElements.get('rheidon-ev-fit')) {
    customElements.define('rheidon-ev-fit', RheidonEvFit);
  }
}
